import "./UpdateOrderComponent.scss";
import template from "./UpdateOrderComponent.html";
import {UserConfig} from "../../../types/UserConfig";
import {IAngularEvent, IFormController, IOnInit, IScope} from "angular";
import {PriceStrategy} from "../../../components/inputs/price/input/PriceStrategy";
import {PriceStrategyService} from "../../../components/inputs/price/service/PriceStrategyService";
import {ComponentCallback} from "../../../types/AngularJS";
import {OrderValidation} from "../../../portfolio/templates/ticket/util/validation/OrderValidation";
import {isTmcOrderViewModel, OrderViewModel} from "../../models/OrderViewModel";
import {
    OptionQuoteViewModel,
    TmcQuoteViewModel,
    UnderlyingQuoteViewModel,
    WarrantQuoteViewModel
} from "../../../quote/models/QuoteViewModel";
import {barColourReverse} from "../../../js/ui.utils";
import {Exchange} from "../../../instruments/exchange/Exchange";
import {AmendOrderRecord} from "../../models/AmendOrderRecord";
import {OrderSide} from "../../models/OrderRecord";
import {Condition} from "../../models/OrderConditions";
import {isConditionalIressOrderViewModel} from "../../models/iress/IressOrderViewModel";
import {isConditionalSimulatedOrderViewModel} from "../../models/simulated/SimulatedOrderViewModel";
import {EquityTicketService} from "../../../portfolio/templates/ticket/equity-ticket/EquityTicketService";
import {AccountBriefRecord} from "../../../account/types/AccountBriefRecord";

export const UpdateOrder = "tfUpdateOrder";

export function tfUpdateOrder() {
    return {
        template,
        bindings: {
            model: "<",
            account: "<",
            onChange: "&",
            onClose: "&",
            onCancel: "&"
        },
        controller: UpdateOrderComponentController,
        controllerAs: "ctrl"
    };
}

const POSITIVE_CHANGE_CLASS = "summary-green";
const NEGATIVE_CHANGE_CLASS = "summary-red";

export class UpdateOrderComponentController implements IOnInit {

    /** @input */
    public readonly model: OrderViewModel;

    /** @input */
    public readonly account: AccountBriefRecord;

    /** @output */
    public readonly onChange: ComponentCallback;

    /** @output */
    public readonly onClose: ComponentCallback;

    /** @output */
    public readonly onCancel: ComponentCallback;

    /** @ui-binding */
    public readonly orderForm: IFormController;

    public isNotOption = false;
    public showQuantityAmend = false;

    public limit: number;
    public quantity: number;

    public minPrice: number | undefined;
    public minQuantity: number;
    public limitChangeClass = "";
    public premiumChangeClass = "";
    public allowZero = false;

    public isConditional = false;
    public condition: Condition;
    public conditionPrice: number;

    protected processing = false;
    private priceStrategy: PriceStrategy;

    /** @ngInject */
    public constructor(private readonly $scope: IScope,
                       public readonly userConfig: UserConfig,
                       private readonly priceStrategyService: PriceStrategyService,
                       private readonly equityTicketService: EquityTicketService,
                       private readonly confirmationService: ConfirmationService) {
    }

    public $onInit() {
        this.limit = this.model.limit!;
        this.quantity = this.model.quantity;
        this.minQuantity = Math.max(this.model.filledQuantity, 1);
        this.priceStrategy = this.model.priceStrategy;
        this.$scope.$on("outsideClick", this.onOutsideClick.bind(this));
        this.isNotOption = this.model.exchange !== Exchange.AOM;
        const isAdviser = this.userConfig.adviser != null;
        this.showQuantityAmend = isAdviser;
        this.allowZero = isTmcOrderViewModel(this.model);

        if (isConditionalIressOrderViewModel(this.model) || isConditionalSimulatedOrderViewModel(this.model)) {
            this.isConditional = true;
            this.condition = this.model.conditions.condition;
            this.conditionPrice = this.model.conditions.price;
        } else {
            this.isConditional = false;
        }
    }

    private async onOutsideClick(event: IAngularEvent) {
        if (this.orderForm.$valid && this.hasChanged) {
            event.preventDefault();
            try {
                await this.confirmationService.confirm("saveOrderChanges");
                this.update();
            } catch (e) {
                this.onClose();
            }
        }
    }

    public get disableControls(): boolean {
        return !this.model.isAmendable || this.processing || this.model.isCompleted;
    }

    public get limitHasChanged(): boolean {
        return this.limit !== this.model.limit;
    }

    public get quantityHasChanged(): boolean {
        return this.quantity !== this.model.quantity;
    }

    public get hasChanged(): boolean {
        return this.limitHasChanged || this.quantityHasChanged;
    }

    public get initialPremium(): number {
        if (this.model.limit == null) {
            return 0;
        } else {
            return this.model.quantity * this.model.limit * this.model.cvf;
        }
    }

    public get currentPremium(): number {
        return this.limit * this.quantity * this.model.cvf;
    }

    public get premiumChange() {
        return this.currentPremium - this.initialPremium;
    }

    public get tradedBarColour() {
        return barColourReverse(this.model.tradedPercent);
    }

    public tickUp() {
        if (this.priceStrategy != null) {
            const newLimit = this.priceStrategy.tickUp(this.limit);
            this.limitChanged(newLimit);
        }
    }

    public tickDown() {
        if (this.priceStrategy != null) {
            const newLimit = this.priceStrategy.tickDown(this.limit);
            this.limitChanged(newLimit);
        }
    }

    public setToBid() {
        if ((this.model.quote != null) && (this.model.quote.bid != null)) {
            this.limitChanged(this.model.quote.bid);
        }
    }

    public setToOffer() {
        if ((this.model.quote != null) && (this.model.quote.ask != null)) {
            this.limitChanged(this.model.quote.ask);
        }
    }

    public limitChanged(newLimit: number) {
        this.limit = newLimit;
        if (!this.hasChanged) {
            this.limitChangeClass = "";
        } else if (this.model.side === OrderSide.BUY || isTmcOrderViewModel(this.model)) {
            this.limitChangeClass = (this.limit < this.model.limit!) ? POSITIVE_CHANGE_CLASS : NEGATIVE_CHANGE_CLASS;
        } else {
            this.limitChangeClass = (this.limit > this.model.limit!) ? POSITIVE_CHANGE_CLASS : NEGATIVE_CHANGE_CLASS;
        }
        this.calculatePremiumChangeClass();
        this.validateTradingRange();
        this.validateTradingState();
        this.validateLimit(newLimit);
    }

    public update() {
        try {
            this.processing = true;
            let change: AmendOrderRecord = {};
            if (this.limitHasChanged) {
                change = Object.assign({}, change, {limit: this.limit, priceInstruction: this.model.pricingInstruction});
            }
            if (this.quantityHasChanged) {
                change = Object.assign({}, change, {quantity: this.quantity});
            }
            this.onChange({change});
        } finally {
            this.processing = false;
        }
    }

    private validateLimit(newLimit: number): boolean {
        const parentLimit = this.model.parentOrderLimit;
        const isParentLimitReached = parentLimit != null && ((this.model.side === OrderSide.BUY && newLimit > parentLimit) ||
            (new Set([OrderSide.SHORT_SELL, OrderSide.LONG_SELL]).has(this.model.side)  && newLimit < parentLimit));
        this.orderForm["price"].$setValidity("parent-limit", !isParentLimitReached, this.orderForm["price"]);
        return !isParentLimitReached;
    }

    private validateTradingRange(): boolean {
        const quoteModel = this.model.quote;

        if (quoteModel == null) {
            this.orderForm["price"].$setValidity("trading-range-limit", true, this.orderForm["price"]);
            return true;
        } else if ((quoteModel instanceof UnderlyingQuoteViewModel) || (quoteModel instanceof WarrantQuoteViewModel)) {
            this.orderForm["price"].$setValidity("trading-range-limit", true, this.orderForm["price"]);
            return true;
        } else if ((quoteModel instanceof OptionQuoteViewModel) || (quoteModel instanceof TmcQuoteViewModel)) {
            const side = this.model.side;

            const validation = OrderValidation.validateEtr(this.limit, side, quoteModel.quote, this.userConfig.adviser != null);
            const isValid = !validation.outsideEtr;
            this.orderForm["price"].$setValidity("trading-range-limit", isValid, this.orderForm["price"]);
            return isValid;
        } else {
            throw Error(`Unsupported Quote Model`);
        }
    }

    private validateTradingState() {
        if (this.model.quote == null) {
            this.orderForm.$setValidity("trading-state", true, this.orderForm);
        } else {
            const notSuspended = this.model.quote.tradingState !== "SUSPENDED";
            this.orderForm.$setValidity("trading-state", notSuspended, this.orderForm);
        }
    }

    public quantityChanged() {
        this.calculatePremiumChangeClass();
    }

    private calculatePremiumChangeClass() {
        const change = this.premiumChange;
        if (!this.hasChanged) {
            this.premiumChangeClass = "";
        } else if (this.model.side === OrderSide.BUY || isTmcOrderViewModel(this.model)) {
            this.premiumChangeClass = (change < 0) ? POSITIVE_CHANGE_CLASS : NEGATIVE_CHANGE_CLASS;
        } else {
            this.premiumChangeClass = (change > 0) ? POSITIVE_CHANGE_CLASS : NEGATIVE_CHANGE_CLASS;
        }
    }

    public reset() {
        this.limit = this.model.limit!;
        this.quantity = this.model.quantity;
        this.orderForm["price"].$setValidity("parent-limit", true, this.orderForm["price"]);
    }

    public openChildOrderModal() {
        this.equityTicketService.openChildOrderWizard(this.model, this.account);
    }

    public cancelOrder() {
        try {
            this.processing = true;
            this.onCancel({order: this.model});
        } finally {
            this.processing = false;
        }
    }
}
